// https://github.com/ueberdosis/tiptap/discussions/2871
// https://github.com/ueberdosis/tiptap/issues/2447

import { Editor } from '@tiptap/react';
import { MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useQueryClient } from '@tanstack/react-query';

import { getDocumentQueryData, useSave } from '../api';
import { THeading, TDocument, TVariant } from '../types';
import { TTreeHeading } from '../tiptap/nodes/heading/HeadingNode';

import { ToastContext } from '@shared/contexts';
import { useLog } from '@modules/users';

import { collectNodesOfType, collectBlocksSaveInfo } from '../helpers/collectNodes.helper';
import findDuplicate from '../helpers/findDuplicate.helper';
import { isRegistrationSettled } from '../helpers/isRegistrationSettled.helper';
import flattenTreeHeadings from '../helpers/flattenTreeHeadings.helper';
import { extractFieldValues } from '../helpers/extractFieldValues';
import { extractTemplateFields } from '../helpers/extractTemplateFields';
import { Transaction } from '@tiptap/pm/state';
import { useDocumentSavingStore } from '../stores/useDocumentSavingStore';
import axios from 'axios';
import { TERMINOLOGY } from '@shared/i18n';
import cleanForSave from '../helpers/cleanForSave.helper';
import { isVersionBound } from '../helpers/isVersionBound.helper';
import { isSystemTr, systemTrKeepsAutoSave } from '../tiptap/plugins/shared';
import { getOrderedLiteratureIds } from '../tiptap/nodes/literatureCitation/plugins/getAutoIndexLiteratureCitationsPlugin';
import getDocumentConfig from '../helpers/getDocumentConfig.helper';
import { documentToSave } from '../largeDocument/model/documentToSave';
import { isSwapping } from '../largeDocument/editor/controller';
import {
  hasFailedEditorOperation,
  hasPendingEditorOperations,
  waitForPendingEditorOperations,
} from '../helpers/pendingEditorOperations.helper';
import { EDITOR_OPERATION_FAILED_MESSAGE } from '../constants';

type TSaveArgs = {
  onSuccess?: () => void;
  onFinished?: () => void;
  forceSave?: boolean;
  isAutoSave?: boolean;
};

export type TSave = (args?: TSaveArgs) => Promise<boolean>;

type TEditorCallback = ({ editor, transaction }: { editor: Editor; transaction: Transaction }) => Editor;
type TAcknowledgeSavedContent = (editor: Editor, content: string) => void;

class DocSaveError extends Error {
  constructor(
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'DocSaveError';
  }
}

const DEBOUNCE_DELAY = 3 * 1000;
const DEBOUNCE_MAX_WAIT = 20 * 1000;

/**
 * 1) Subscribe to Tiptap's `update` event to detect doc changes.
 * 2) Debounce to avoid saving on every keystroke.
 * 3) Manage states (isSaved, isError, lastSavedAt) via Zustand.
 */
const useAutoSave = (
  doc: TDocument,
  isEdit: boolean,
  requestEdit: boolean,
  refBlockAutoSave: MutableRefObject<boolean>,
  isTransitioningVariant: boolean,
  refTreeHeadings: MutableRefObject<TTreeHeading[]>,
  editor: Editor | null | undefined,
  acknowledgeSavedContent: TAcknowledgeSavedContent,
  variant?: TVariant,
  templateDropdownOptionsRef?: MutableRefObject<Record<string, string[]>>
): { save: TSave; isExplicitSavePending: boolean } => {
  const { show } = useContext(ToastContext);
  const logMutation = useLog();
  const saveMutation = useSave();
  const queryClient = useQueryClient();

  const { isSaved, setIsSaved, setIsError, setLastSavedAt } = useDocumentSavingStore();
  const [pendingExplicitSaves, setPendingExplicitSaves] = useState(0);

  // Debounce auto-save by 3s; force-save after 20s of continuous typing.
  const debounceChanges = useDebouncedCallback(() => save({ isAutoSave: true }), DEBOUNCE_DELAY, {
    maxWait: DEBOUNCE_MAX_WAIT,
  });

  // Refs to track current doc, editor, variant, etc. in the `save` callback:
  const refDoc = useRef(doc);
  const refEditor = useRef(editor);
  const refIsEdit = useRef(isEdit);
  const refRequestEdit = useRef(requestEdit);
  const refVariant = useRef(variant);
  const refIsSaved = useRef(isSaved);
  const refSaveQueue = useRef<Promise<void>>(Promise.resolve());
  const refContentRevision = useRef(0);
  const refSavedRevision = useRef(isSaved ? 0 : -1);
  const refAcknowledgeSavedContent = useRef(acknowledgeSavedContent);

  refDoc.current = doc;
  refEditor.current = editor;
  refIsEdit.current = isEdit;
  refRequestEdit.current = requestEdit;
  refVariant.current = variant;
  refIsSaved.current = isSaved;
  refAcknowledgeSavedContent.current = acknowledgeSavedContent;

  const throwDocSaveError = useCallback((code: string, debugMsg: string) => {
    console.error(debugMsg);
    logMutation.mutate({ message: debugMsg });

    const userMsg = `Cannot save content. Reload the page or undo the last few actions! (Code: ${code})`;
    show('error', userMsg);

    throw new DocSaveError(code, userMsg);
  }, []);

  const performSave: TSave = useCallback(
    async ({ onSuccess = () => {}, onFinished = () => {}, forceSave = false, isAutoSave = false } = {}) => {
      const num = Math.random().toString().slice(2, 6);
      try {
        if (!refDoc.current.draft || !refEditor.current) return false;

        if (refBlockAutoSave.current && isAutoSave) {
          // can still happen if the user switches to view mode with poor internet connection
          console.log(`[AutoSave ${num}] blocked auto save`);
          return false;
        }

        const savingEditor = refEditor.current;
        const savingDoc = refDoc.current;
        const savingVariant = refVariant.current;
        let waitedForOperations = false;
        // Loop: work inserted while waiting must also finish before the HTML is captured.
        while (!isAutoSave && hasPendingEditorOperations(savingEditor, savingDoc._id, refRequestEdit.current)) {
          waitedForOperations = true;
          try {
            await waitForPendingEditorOperations(savingEditor, savingDoc._id, refRequestEdit.current);
          } catch {
            if (hasFailedEditorOperation(savingEditor)) {
              logMutation.mutate({ message: `EDITOR_OPERATION_FAILED documentId=${savingDoc._id}` });
              show('error', EDITOR_OPERATION_FAILED_MESSAGE);
            } else show('error', 'Content is still being prepared or could not finish. Please try saving again.');
            throw new DocSaveError('PENDING_OPERATIONS', 'Editor operations did not settle before saving.');
          }
        }
        if (savingEditor.isDestroyed || (!isAutoSave && refRequestEdit.current && !refIsEdit.current)) return false;
        if (
          waitedForOperations &&
          (savingEditor !== refEditor.current ||
            savingDoc._id !== refDoc.current._id ||
            savingDoc.draft?.blobName !== refDoc.current.draft?.blobName ||
            savingVariant?.folder !== refVariant.current?.folder)
        )
          return false;

        if (refSavedRevision.current === refContentRevision.current && !forceSave && !waitedForOperations) {
          onSuccess();
          return true;
        }

        console.log(`[AutoSave ${num}] saving... (forceSave=${forceSave})`);
        refIsSaved.current = false;
        setIsSaved(false);

        // A registration's cache write lands before React re-renders refDoc, so explicit saves read the cache.
        const currentDoc = !isAutoSave
          ? (getDocumentQueryData(queryClient, savingDoc._id)?.doc ?? refDoc.current)
          : refDoc.current;
        if (!currentDoc.draft || (!isAutoSave && currentDoc.draft.blobName !== savingDoc.draft?.blobName)) return false;
        const isBound = isVersionBound(currentDoc.draft);
        // A save can start before the tab plugin's animation frame (notably AI apply / leaving edit).
        savingEditor.commands.flushTabLayout();
        const capturedRevision = refContentRevision.current;

        // The whole document, captured before the await so it reflects the exact state being uploaded
        // (no race with user typing). Everything below reads it - never the editor - because a large
        // document has only one part in the editor and every list here REPLACES what is stored.
        const { headingStart } = getDocumentConfig(currentDoc.draft.config);
        const document = documentToSave(savingEditor, headingStart, refTreeHeadings.current);
        const currentHtml = document.html;
        const title = document.title;

        // Bound docs also have the full composed content in the editor, so validation runs the same way.
        let refs = collectNodesOfType(document.doc, 'reference');
        const dupRef = findDuplicate(refs, (node) => node.attrs.id);
        if (dupRef) throwDocSaveError('A', `Duplicate reference found ${dupRef.attrs.id} in ${doc._id}.`);
        /**
         * Only registered references - but `loadedId` cannot answer that on its own any more.
         *
         * It is a node view's record that its own registration settled, and a reference outside the
         * part the editor holds has no node view to update it. Filtering on it alone drops every
         * reference in the rest of the document, and the server REPLACES `outRefIds` with this list.
         * `doc.refsData` is what the backend actually knows, and it does not depend on mounting.
         */
        const registeredRefs = currentDoc.refsData ?? {};
        refs = refs.filter((r) => isRegistrationSettled(r.attrs) || r.attrs.id in registeredRefs);

        const literatureIds = getOrderedLiteratureIds(document.doc);

        const headings: THeading[] = flattenTreeHeadings(document.headings);
        const dupH = findDuplicate(headings, (h) => h.id);
        if (dupH) throwDocSaveError('B', `Duplicate heading found ${dupH.id} in ${doc._id}.`);

        const blocksSaveInfo = collectBlocksSaveInfo(document.doc, new Set(Object.keys(currentDoc.blocksData ?? {})));
        const dupBlk = findDuplicate(blocksSaveInfo, (b) => b.id);
        if (dupBlk)
          throwDocSaveError('C', `Duplicate ${TERMINOLOGY.blockEither.lower} found ${dupBlk.id} in ${doc._id}.`);

        let savedContent: string;
        if (isBound) {
          // BOUND DOCUMENT: extract field values, persist them as JSON.
          // cacheHtml: full composed HTML cached for instant view-mode rendering
          // (avoids re-fetch + re-compose delay when switching from edit to view).
          // Strip transient markup (the NEW comment placeholder, image provenance) from each field
          // value before persisting, mirroring cleanForSave on the body blob; committed comment ids stay.
          const fieldValues = Object.fromEntries(
            Object.entries(extractFieldValues(document.doc)).map(([fieldId, value]) => [fieldId, cleanForSave(value)])
          );
          const cacheHtml = cleanForSave(currentHtml);
          await saveMutation.mutateAsync({
            doc: currentDoc,
            fieldValues,
            cacheHtml,
            title,
            headings,
            outRefIds: refs.map((node) => node.attrs.id),
            literatureIds,
            blocksSaveInfo,
          });
          savedContent = cacheHtml;
        } else {
          // REGULAR DOCUMENT: full HTML blob
          const html = cleanForSave(currentHtml);

          // For templates, also extract field definitions and merge dropdown options from the ref.
          const templateFields = currentDoc.isTemplate ? extractTemplateFields(document.doc) : undefined;
          if (templateFields && templateDropdownOptionsRef) {
            let fieldValues: Record<string, string> | undefined;
            for (const field of templateFields) {
              if (field.type === 'DROPDOWN') {
                const refOptions = templateDropdownOptionsRef.current?.[field.fieldId];
                if (refOptions?.length) {
                  field.options = refOptions;
                } else {
                  // Lazy-evaluate: only extract field values when a dropdown is missing options in the ref.
                  if (!fieldValues) fieldValues = extractFieldValues(document.doc);
                  console.warn(
                    `[useAutoSave] No options in ref for dropdown ${field.fieldId}, using selectedValue fallback`
                  );
                  field.options = [fieldValues[field.fieldId] || 'Option 1'];
                }
              }
            }
          }

          await saveMutation.mutateAsync({
            doc: currentDoc,
            html,
            title,
            headings,
            outRefIds: refs.map((node) => node.attrs.id),
            literatureIds,
            blocksSaveInfo,
            variant: savingVariant,
            templateFields,
          });
          savedContent = html;
        }

        refAcknowledgeSavedContent.current(savingEditor, savedContent);

        console.log(`[AutoSave ${num}] success!`);
        refSavedRevision.current = Math.max(refSavedRevision.current, capturedRevision);
        const isCurrentRevisionSaved = refSavedRevision.current === refContentRevision.current;
        refIsSaved.current = isCurrentRevisionSaved;
        setIsSaved(isCurrentRevisionSaved);
        setIsError(false);
        setLastSavedAt(Date.now());
        if (!isAutoSave && !isCurrentRevisionSaved) return false;
        onSuccess();
        return true;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        console.error(`[AutoSave ${num}] error =>`, message);

        // only show generic toast for truly unexpected JS errors
        if (!(err instanceof DocSaveError) && !axios.isAxiosError(err)) {
          show('error', 'An unexpected error occurred. Please try again.');
        }

        setIsError(true);
        refIsSaved.current = false;
        return false;
      } finally {
        onFinished();
      }
    },
    []
  );

  const save: TSave = useCallback(async (args = {}) => {
    if (!args.isAutoSave) {
      debounceChanges.cancel();
      setPendingExplicitSaves((count) => count + 1);
    }

    // Registration may settle before this queue slot runs, so remember now that a write is needed.
    const pendingAtRequest =
      !args.isAutoSave &&
      refEditor.current &&
      hasPendingEditorOperations(refEditor.current, refDoc.current._id, refRequestEdit.current);
    const saveArgs = pendingAtRequest ? { ...args, forceSave: true } : args;

    const queuedSave = refSaveQueue.current.then(
      () => performSave(saveArgs),
      () => performSave(saveArgs)
    );
    refSaveQueue.current = queuedSave.then(
      () => undefined,
      () => undefined
    );
    try {
      return await queuedSave;
    } finally {
      if (!args.isAutoSave) setPendingExplicitSaves((count) => count - 1);
    }
  }, []);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate: TEditorCallback = ({ editor: e, transaction: t }) => {
      if (!refRequestEdit.current || t.steps.length === 0) return e;
      // System transactions (plugin appendTransactions, status-nonce bumps) mutate
      // derived state only - they must not schedule an autosave or the citation
      // status refresh loop keeps re-saving the doc. A system write that lands in the saved html says so.
      if (isSystemTr(t) && !systemTrKeepsAutoSave(t)) return e;
      // A part swap replaces content the model already holds - not an edit, so it must not mark the
      // document unsaved.
      if (isSwapping()) return e;
      // If the transaction was blocked by filterTransaction (e.g. TemplateLocking), the editor
      // state's doc is still the pre-transaction doc (same object reference). Avoid triggering
      // an auto-save for changes that were never applied.
      if (e.state.doc === t.before) return e;
      refContentRevision.current += 1;
      if (refIsSaved.current) {
        refIsSaved.current = false;
        setIsSaved(false);
      }

      if (refIsEdit.current && !refBlockAutoSave.current) debounceChanges();

      return e;
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  useEffect(() => () => debounceChanges.cancel(), []);

  useEffect(() => {
    // If user leaves edit mode or changes variant, consider doc "saved"
    if (!requestEdit || isTransitioningVariant) {
      refSavedRevision.current = refContentRevision.current;
      refIsSaved.current = true;
      setIsSaved(true);
    }
  }, [requestEdit, isTransitioningVariant]);

  return { save, isExplicitSavePending: pendingExplicitSaves > 0 };
};

export default useAutoSave;
